import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Modal } from "semantic-ui-react";

import moment from "moment";
import { useIntl } from "react-intl";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import EditorColumn from "react-lib/appcon/common/EditorColumn";

import SnackMessage from "react-lib/apps/common/SnackMessage";

// Common
import ModLotNoExpUX from "./ModLotNoExpUX";

// Utils
import { formatDate } from "react-lib/utils/dateUtils";

// Types
type ModLotNoExpProps = {
  onEvent: (e: any) => any;
  setProp: (key: string, value: any, callback?: () => void) => any;
  data?: Record<string, any> | null;
  lotNoExpList?: Record<string, any>[];
  open: boolean;
  orderPerformDiv?: number | string;
  readOnly?: boolean;
  type: "drug" | "supply";
  buttonLoadCheck?: Record<string, any>;
  errorMessage?: Record<string, any>;
  languageUX: string;
  onClose: () => void;
};

// Const
const MOD_LOT_NO_EXP = "ModLotNoExp";

const ModLotNoExp = (props: ModLotNoExpProps) => {
  const intl = useIntl();
  const [lotList, setLotList] = useState<Record<string, any>[]>([]);

  useEffect(() => {
    if (props.open && props.data?.product) {
      props.setProp("lotNoExpList", []);

      props.onEvent({
        message: "GetLotNoExp",
        params: {
          orderItem: props.data.id,
          orderPerformDiv: props.orderPerformDiv,
          productId: props.data.product,
          type: props.type,
        },
      });
    }
  }, [props.data, props.open, props.type]);

  useEffect(() => {
    setLotList(props.lotNoExpList ?? []);
  }, [props.lotNoExpList]);

  const handleChangeEdit = useCallback(
    (data: { key: string; index: number; item: any; value: string }) => {
      const list = [...lotList];

      let value = data.value as string;

      if (Number(value) < 0) {
        value = "";
      } else if (Number(value) > Number(data.item.onh_qty)) {
        value = data.item.onh_qty;
      }

      list[data.index][data.key] = Number(value) ? Math.ceil(Number(value)) : "";
      list[data.index]["is_change"] = true;

      setLotList(list);
    },
    [lotList]
  );

  const alignCenter = useCallback(
    (item: any, keys: string) =>
      Object.fromEntries(
        keys.split(",").map((key) => [
          key,
          <div key={key} style={{ display: "flex", justifyContent: "center" }}>
            {(item as any)[key]}
          </div>,
        ])
      ),
    []
  );

  const lotItems = useMemo(
    () =>
      lotList.map((item, index) => ({
        ...item,
        disp_qty: (
          <EditorColumn
            // style
            backgroundColor="rgb(255, 255, 204)"
            disabled={props.readOnly}
            value={item.disp_qty}
            selectOnFocus
            valueOnFocus
            onDataChanged={(value: string) => {
              handleChangeEdit({ index, item, key: "disp_qty", value });
            }}
          />
        ),
        ...alignCenter(
          {
            exp: item.lot?.exp_datetime ? formatDate(moment(item.lot.exp_datetime)) : "",
            lot_no: item.lot?.mfc_no || "",
            unit: item.product?.unit_name || "",
            onh_qty: item.quantity,
          },
          "lot_no,exp,onh_qty,unit"
        ),
      })),
    [lotList, props.readOnly]
  );

  const allowedEdit = useMemo(() => {
    const sum = lotList.reduce((result, item) => result + Number(item.disp_qty), 0);
    const quantity = props.data?.quantity ?? props.data?.quantity_issue;

    return !!quantity && Number(quantity) === sum;
  }, [lotList, props.data?.quantity, props.data?.quantity_issue]);

  const handleSave = (e: any, data: any) => {
    props.onEvent({
      message: "UpdateLotNoExp",
      params: {
        action: data.name,
        card: MOD_LOT_NO_EXP,
        data: lotList.filter((item) =>
          !item.plan_id && !Number(item.disp_qty) ? false : item.is_change
        ),
        orderItem: props.data?.id,
        type: props.type,
        onSuccess: handleClose,
      },
    });
  };

  const handleClose = () => {
    props.setProp("lotNoExpList", []);

    props.onClose();
  };

  return (
    <>
      <SnackMessage
        onEvent={props.onEvent}
        error={props.errorMessage?.[MOD_LOT_NO_EXP]}
        success={null}
        languageUX={props.languageUX}
        onClose={() => {
          props.setProp(`errorMessage.${MOD_LOT_NO_EXP}`, null);
        }}
      />

      <Modal
        style={{ width: "40%" }}
        open={props.open}
        size="small"
        closeOnDimmerClick
        onClose={handleClose}
      >
        <ModLotNoExpUX
          // data
          lotList={lotItems}
          languageUX={props.languageUX}
          // callback
          onClose={handleClose}
          // Element
          ButtonSave={
            !props.readOnly && (
              <ButtonLoadCheck
                // function
                setProp={props.setProp}
                color="green"
                // config
                disabled={!allowedEdit}
                name="SAVE"
                // data
                paramKey={`${MOD_LOT_NO_EXP}_SAVE`}
                size="small"
                title={intl.formatMessage({ id: "ยืนยัน" })}
                buttonLoadCheck={props.buttonLoadCheck?.[`${MOD_LOT_NO_EXP}_SAVE`]}
                onClick={handleSave}
              />
            )
          }
          name={`[${props.data?.code || ""}] ${props.data?.name || ""} Qty ${
            props.data?.quantity ?? props.data?.quantity_issue ?? ""
          } ${props.data?.stock_unit_name || props.data?.stock_unit || ""}`}
        />
      </Modal>
    </>
  );
};

// lot_no: "1A/003",
// exp: "31/12/2565",
// onh_qty: "65",
// disp_qty: "3",
// unit: "Tab",

export default React.memo(ModLotNoExp);
